"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useAuth } from "@/components/auth/auth-provider"
import { fetchWebsocketCredentialsFromSession } from "@/lib/identity-session"
import {
  parseWebsocketCredentials,
  type SessionClientBootstrap,
  type WebsocketCredentials,
} from "@/lib/identity-setup"
import { useAppSelector } from "@/lib/store"
import { wsWarn } from "@/lib/websocket/logger"
import {
  acquireSharedWebSocketAsync,
  forceReleaseSharedWebSocket,
  getSharedWebSocket,
  registerWebsocketProviderMount,
  unregisterWebsocketProviderMount,
  type ClientWebSocket,
  type RealtimeCursor,
  type RealtimeInvalidateMessage,
  type RealtimeScope,
  type WsConnectionState,
  type WsEventHandler,
  type WsMessage,
} from "@/lib/websocket"

type RealtimeScopeHandler = (frame?: RealtimeInvalidateMessage) => Promise<void> | void

type WebsocketContextValue = {
  connectionState: WsConnectionState
  on: (event: string, handler: WsEventHandler) => () => void
  adoptRealtimeCursor: (cursor: RealtimeCursor | null | undefined) => void
  registerRealtimeScope: (scope: RealtimeScope, handler: RealtimeScopeHandler) => () => void
}

const WebsocketContext = createContext<WebsocketContextValue | null>(null)

const REALTIME_SCOPES: RealtimeScope[] = ["active_call", "dialer_session"]

function isRealtimeInvalidate(message: WsMessage): message is RealtimeInvalidateMessage {
  return (
    message.event === "realtime_invalidate" &&
    typeof message.window_id === "string" &&
    typeof message.seq === "number" &&
    Array.isArray(message.scopes)
  )
}

function frameCursor(frame: RealtimeInvalidateMessage): RealtimeCursor {
  return {
    window_id: frame.window_id,
    seq: frame.seq,
    prev_window_id: frame.prev_window_id ?? null,
    prev_seq_max: frame.prev_seq_max ?? null,
  }
}

function credentialsFromBootstrap(
  bootstrap: SessionClientBootstrap | null | undefined
): WebsocketCredentials | null {
  return parseWebsocketCredentials(bootstrap?.websocket) ?? null
}

export function WebsocketProvider({
  children,
  bootstrap: bootstrapProp,
}: {
  children: ReactNode
  bootstrap?: SessionClientBootstrap | null
}) {
  const { authenticated } = useAuth()
  const bootstrapRedux = useAppSelector((s) => s.sessionBootstrap.data)
  const bootstrap = bootstrapProp ?? bootstrapRedux
  const wsToken = bootstrap?.websocket?.token

  const clientRef = useRef<ClientWebSocket | null>(null)
  const connectedTokenRef = useRef<string | null>(null)
  const realtimeCursorRef = useRef<RealtimeCursor | null>(null)
  const realtimeHandlersRef = useRef<Record<RealtimeScope, Set<RealtimeScopeHandler>>>({
    active_call: new Set(),
    dialer_session: new Set(),
  })
  const realtimeScopeStateRef = useRef<
    Record<
      RealtimeScope,
      {
        inFlight: boolean
        pending: boolean
        timer: number | null
        latestFrame: RealtimeInvalidateMessage | undefined
      }
    >
  >({
    active_call: { inFlight: false, pending: false, timer: null, latestFrame: undefined },
    dialer_session: { inFlight: false, pending: false, timer: null, latestFrame: undefined },
  })
  const [connectionState, setConnectionState] = useState<WsConnectionState>("idle")

  const adoptRealtimeCursor = useCallback((cursor: RealtimeCursor | null | undefined) => {
    realtimeCursorRef.current =
      cursor && cursor.window_id && Number.isFinite(cursor.seq) ? cursor : null
  }, [])

  const stageScopeFrame = useCallback((scope: RealtimeScope, frame: RealtimeInvalidateMessage | undefined) => {
    realtimeScopeStateRef.current[scope].latestFrame = frame
  }, [])

  const runScope = useCallback((scope: RealtimeScope, frame?: RealtimeInvalidateMessage) => {
    const state = realtimeScopeStateRef.current[scope]
    if (frame !== undefined) {
      state.latestFrame = frame
    }
    if (state.inFlight) {
      state.pending = true
      return
    }
    if (state.timer !== null) return
    state.timer = window.setTimeout(() => {
      state.timer = null
      void (async () => {
        state.inFlight = true
        try {
          do {
            state.pending = false
            const handlers = Array.from(realtimeHandlersRef.current[scope])
            const latestFrame = state.latestFrame
            state.latestFrame = undefined
            await Promise.all(handlers.map((handler) => Promise.resolve(handler(latestFrame))))
          } while (state.pending)
        } finally {
          state.inFlight = false
        }
      })()
    }, 0)
  }, [])

  const runScopes = useCallback(
    (scopes: RealtimeScope[], frame?: RealtimeInvalidateMessage) => {
      const unique = Array.from(new Set(scopes.filter((scope) => REALTIME_SCOPES.includes(scope))))
      for (const scope of unique) {
        runScope(scope, frame)
      }
    },
    [runScope],
  )

  const runFullPull = useCallback(() => {
    for (const scope of REALTIME_SCOPES) {
      runScope(scope, undefined)
    }
  }, [runScope])

  const registerRealtimeScope = useCallback((scope: RealtimeScope, handler: RealtimeScopeHandler) => {
    realtimeHandlersRef.current[scope].add(handler)
    return () => {
      realtimeHandlersRef.current[scope].delete(handler)
    }
  }, [])

  const requestMissingFrames = useCallback(
    (windowId: string, afterSeq: number, throughSeq: number) => {
      const client = clientRef.current ?? getSharedWebSocket()
      return client?.requestRealtimeFrames({
        windowId,
        afterSeq,
        throughSeq,
      })
    },
    [],
  )

  const applyRealtimeFrame = useCallback(
    async (frame: RealtimeInvalidateMessage) => {
      const cursor = realtimeCursorRef.current
      if (cursor === null) {
        runFullPull()
        return
      }

      if (frame.window_id === cursor.window_id) {
        if (frame.seq === cursor.seq + 1) {
          realtimeCursorRef.current = frameCursor(frame)
          runScopes(frame.scopes, frame)
          return
        }
        if (frame.seq <= cursor.seq) {
          return
        }

        const response = await requestMissingFrames(cursor.window_id, cursor.seq, frame.seq)
        const frames = response?.event === "realtime_frames" ? response.frames ?? [] : []
        if (frames.length === frame.seq - cursor.seq) {
          let expected = cursor.seq + 1
          const scopes = new Set<RealtimeScope>()
          const latestByScope = new Map<RealtimeScope, RealtimeInvalidateMessage>()
          for (const replayFrame of frames) {
            if (replayFrame.window_id !== cursor.window_id || replayFrame.seq !== expected) {
              runFullPull()
              return
            }
            for (const scope of replayFrame.scopes) {
              scopes.add(scope)
              latestByScope.set(scope, replayFrame)
            }
            realtimeCursorRef.current = frameCursor(replayFrame)
            expected += 1
          }
          for (const scope of scopes) {
            stageScopeFrame(scope, latestByScope.get(scope))
          }
          for (const scope of scopes) {
            runScope(scope)
          }
          return
        }
        runFullPull()
        return
      }

      if (frame.prev_window_id === cursor.window_id) {
        realtimeCursorRef.current = frameCursor(frame)
        runFullPull()
        return
      }

      runFullPull()
    },
    [requestMissingFrames, runFullPull, runScope, runScopes, stageScopeFrame],
  )

  useEffect(() => {
    if (!authenticated) {
      connectedTokenRef.current = null
      realtimeCursorRef.current = null
      forceReleaseSharedWebSocket()
      clientRef.current = null
      queueMicrotask(() => setConnectionState("disconnected"))
      return
    }

    if (!wsToken) {
      return
    }

    if (connectedTokenRef.current === wsToken && getSharedWebSocket()) {
      const existing = getSharedWebSocket()!
      existing.setOnStateChange(setConnectionState)
      clientRef.current = existing
      return
    }

    registerWebsocketProviderMount()

    let cancelled = false

    void (async () => {
      let creds = credentialsFromBootstrap(bootstrap)
      if (!creds) {
        creds = await fetchWebsocketCredentialsFromSession()
      }
      if (cancelled || !creds) {
        if (!cancelled && !creds) {
          wsWarn(
            true,
            "[ws] cannot connect — set WEBSOCKET_JWT_SECRET on the backend"
          )
        }
        return
      }

      const client = await acquireSharedWebSocketAsync(creds, setConnectionState)
      if (cancelled) return

      connectedTokenRef.current = creds.token
      clientRef.current = client
    })()

    return () => {
      cancelled = true
      unregisterWebsocketProviderMount()
      clientRef.current = getSharedWebSocket()
    }
  }, [authenticated, wsToken, bootstrap])

  useEffect(() => {
    const client = clientRef.current ?? getSharedWebSocket()
    if (!client) return
    return client.on("realtime_invalidate", (message) => {
      if (!isRealtimeInvalidate(message)) return
      void applyRealtimeFrame(message)
    })
  }, [applyRealtimeFrame, connectionState])

  // Full BFF pull only on (re)connect or when frame continuity is lost — not on
  // window focus. Live call/dialer updates come from realtime_invalidate frames.
  useEffect(() => {
    if (connectionState !== "connected") return
    realtimeCursorRef.current = null
    runFullPull()
  }, [connectionState, runFullPull])

  const value = useMemo<WebsocketContextValue>(
    () => ({
      connectionState,
      adoptRealtimeCursor,
      on: (event, handler) => {
        const client = clientRef.current ?? getSharedWebSocket()
        if (!client) {
          return () => {}
        }
        return client.on(event, handler)
      },
      registerRealtimeScope,
    }),
    [adoptRealtimeCursor, connectionState, registerRealtimeScope]
  )

  return (
    <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>
  )
}

export function useWebsocket(): WebsocketContextValue {
  const ctx = useContext(WebsocketContext)
  if (!ctx) {
    throw new Error("useWebsocket must be used inside WebsocketProvider")
  }
  return ctx
}

export type { WsMessage }
